import { defineStore } from 'pinia'

import api from '@/api/auth.js'
import { redirectBackToTenant } from '@/utils/url.js'
import { getSessionStorageData, setSessionStorageData, removeSessionStorageData } from '@/utils/storage.js'

const RECOVERY_STATE_KEY = 'uapBridgeRecoveryState'
const RECOVERY_TIMEOUT_MS = 10000

// Explicit state machine for the in-app "401 -> bridge redirect -> native
// refreshes token -> reload" recovery flow. Replaces the old scattered
// hasAttemptedBridgeRedirect / bridgeRedirectTriggeredThisInstance flags with
// one owned piece of state that both the loading overlay (src/js/app.js) and
// the router guard (src/utils/member.js) read from.
//
//   idle -------> recovering -------> recovered   (nativeJSICallback arrived)
//                      |
//                      +-----------> failed        (timed out / gave up)
export const RECOVERY_STATE = {
  IDLE: 'idle',
  RECOVERING: 'recovering',
  RECOVERED: 'recovered',
  FAILED: 'failed'
}

function hydrateRecoveryState () {
  const persisted = getSessionStorageData(RECOVERY_STATE_KEY)
  return Object.values(RECOVERY_STATE).includes(persisted) ? persisted : RECOVERY_STATE.IDLE
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    isCallingRefreshToken: false,

    // hydrated from sessionStorage so "recovery already attempted this
    // session" survives the full page reload that redirectToBridge()/reload()
    // trigger — a brand new WebView instance gets fresh sessionStorage and so
    // naturally starts back at 'idle', allowed to attempt recovery again
    recoveryState: hydrateRecoveryState(),
    // NOT persisted — true only if THIS page instance is the one that put
    // recovery into 'recovering', so a concurrent, unrelated getMember
    // success (from an overlapping navigation) can't reset recovery state out
    // from under a still-pending redirect this instance triggered
    recoveryTriggeredThisInstance: false,
    // in-memory bookkeeping below, never persisted
    _watchdogArmed: false,
    _watchdogTimeoutId: null,
    _recoveryWaiters: []
  }),
  getters: {
    isRecoveryInProgress: (state) => state.recoveryState === RECOVERY_STATE.RECOVERING
  },
  actions: {
    refreshToken ({ success, fail }) {
      if (!this.isCallingRefreshToken) {
        this.isCallingRefreshToken = true
        api.refreshToken()
        .then(response => {
          success && success(response)
        })
        .catch(error => {
          fail && fail(error)
        })
        .finally(() => {
          this.isCallingRefreshToken = false
        })
      }
    },

    _setRecoveryState (value) {
      const wasRecovering = this.recoveryState === RECOVERY_STATE.RECOVERING
      this.recoveryState = value
      if (value === RECOVERY_STATE.IDLE) {
        removeSessionStorageData(RECOVERY_STATE_KEY)
      } else {
        setSessionStorageData(RECOVERY_STATE_KEY, value)
      }
      if (wasRecovering && value !== RECOVERY_STATE.RECOVERING) {
        if (this._watchdogTimeoutId) {
          clearTimeout(this._watchdogTimeoutId)
          this._watchdogTimeoutId = null
        }
        const waiters = this._recoveryWaiters.splice(0)
        waiters.forEach(resolve => resolve(value))
      }
    },

    // Atomic check-and-set: JS is single-threaded, so of however many
    // concurrent 401s land around the same time in this page instance, only
    // the ONE that observes 'idle' flips it to 'recovering' and returns true
    // — every other caller (and every call after recovery has already been
    // attempted, including one hydrated from a prior instance) gets false and
    // must no-op.
    beginBridgeRecovery () {
      if (this.recoveryState !== RECOVERY_STATE.IDLE) return false
      console.log('[AuthRecovery] recovery starting, bridge redirect about to fire')
      this._setRecoveryState(RECOVERY_STATE.RECOVERING)
      this.recoveryTriggeredThisInstance = true
      this.armRecoveryWatchdog()
      return true
    },

    // Ensures this page instance has a live ~10s watchdog running whenever
    // recovery is in progress. Covers two cases with one mechanism:
    //  1) the instance that just called beginBridgeRecovery() (armed inline,
    //     above)
    //  2) an instance that loaded with 'recovering' already hydrated from a
    //     prior instance's sessionStorage — that prior instance's own timer
    //     died with it (e.g. the bridge redirect's real navigation destroyed
    //     the page), so without re-arming here this instance would otherwise
    //     have no timeout and could spin forever.
    // Safe to call any number of times — only the first call while recovery
    // is in progress actually starts a timer.
    armRecoveryWatchdog (timeoutMs = RECOVERY_TIMEOUT_MS) {
      if (this.recoveryState !== RECOVERY_STATE.RECOVERING) return false
      if (this._watchdogArmed) return false
      this._watchdogArmed = true
      console.log('[AuthRecovery] watchdog armed', { timeoutMs })
      this._watchdogTimeoutId = setTimeout(() => {
        this._watchdogTimeoutId = null
        this.giveUpBridgeRecovery()
      }, timeoutMs)
      return true
    },

    // Called when native's nativeJSICallback fires unmTokenRefreshed. Returns
    // false (no-op) if recovery isn't in progress any more — e.g. it already
    // timed out and gave up — so a late/stale success can't resurrect it.
    markRecoverySucceeded () {
      if (this.recoveryState !== RECOVERY_STATE.RECOVERING) return false
      this._setRecoveryState(RECOVERY_STATE.RECOVERED)
      return true
    },

    // Gives up on recovery and hands control back to native via the existing
    // redirectBackToTenant() mechanism — never a web login redirect. Guarded
    // so the native handoff fires at MOST ONCE no matter how many independent
    // observers (the loading overlay's watchdog, the router guard's wait)
    // notice the same stalled/timed-out recovery.
    giveUpBridgeRecovery () {
      if (this.recoveryState === RECOVERY_STATE.FAILED) return false
      this._setRecoveryState(RECOVERY_STATE.FAILED)
      console.warn('[AuthRecovery] recovery timed out, handing off to native')
      redirectBackToTenant()
      return true
    },

    // Mirrors the old getMemberSuccess guard: a getMember success only clears
    // recovery state back to idle when THIS instance isn't the one waiting on
    // a pending redirect it triggered itself.
    clearRecoveryIfUnowned () {
      if (!this.recoveryTriggeredThisInstance && this.recoveryState !== RECOVERY_STATE.IDLE) {
        this._setRecoveryState(RECOVERY_STATE.IDLE)
      }
    },

    // Resolves once recovery leaves 'recovering' — immediately if it already
    // has (or has always been idle), otherwise when it next settles to
    // 'recovered' or 'failed'. Used by the router guard so a pending
    // navigation eventually completes instead of waiting forever.
    waitForRecoveryToSettle () {
      if (this.recoveryState !== RECOVERY_STATE.RECOVERING) {
        return Promise.resolve(this.recoveryState)
      }
      return new Promise(resolve => { this._recoveryWaiters.push(resolve) })
    }
  }
})
